import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createPreset,
  deletePreset,
  listPresets,
  restoreBuiltinPreset,
  setDefaultPreset,
  updatePreset,
} from '../api';
import type {
  ModelInfo,
  VideoGenConfig,
  VideoGenPreset,
  VideoOutputFormat,
  WatermarkMode,
} from '../types';

type FormState = VideoGenConfig & {
  seedText: string;
  guidanceScaleText: string;
  offsetFrontText: string;
  offsetRearText: string;
  offsetLeftText: string;
  offsetRightText: string;
  heightOffsetFrontText: string;
  heightOffsetRearText: string;
  heightOffsetLeftText: string;
  heightOffsetRightText: string;
  maxWidthText: string;
  featherText: string;
  shadowRetriesText: string;
  shadowDarkeningText: string;
  manualDragFramesText: string;
  sampleStrideText: string;
  sampleStartText: string;
  sampleEndText: string;
  brightLumaText: string;
  brightRatioText: string;
  cleanupPaddingText: string;
  cleanupRetriesText: string;
  cleanupCenterShiftText: string;
  cleanupSizeChangeText: string;
  cleanupAspectChangeText: string;
};

const SHADOW_RESOLUTIONS = ['0.5K', '1K', '2K', '4K'];
const SHADOW_ASPECT_RATIOS = [
  'auto',
  '21:9',
  '16:9',
  '3:2',
  '4:3',
  '5:4',
  '1:1',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
];
const COMPOSE_MODES = ['with_background', 'no_background', 'off'];
const IMAGE_EDIT_MODELS = ['nano-banana-2', 'gpt-image-2'];
const SHADOW_MODELS = [...IMAGE_EDIT_MODELS, 'meta/muse-image/edit'];
const SEAM_MATCH_MODES = ['api', 'local', 'direct'];
const SEAM_MATCH_HINTS: Record<string, string> = {
  api: 'fal.ai Bria remover — ~$2.30 per job at $0.018/call.',
  local: 'CPU silhouette — no background-remover calls.',
  direct: 'CPU edge correlation — no calls, and no silhouette to get wrong.',
};
const SHADOW_QUALITIES = ['low', 'medium', 'high'];
const PLACEMENT_MODES = ['median', 'model'];
const WATERMARK_MODES: Array<{ value: WatermarkMode; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'visible', label: 'Visible logo' },
];
const OUTPUT_FORMATS: Array<{ value: VideoOutputFormat; label: string }> = [
  { value: 'av1-webm', label: 'AV1 / WebM' },
  { value: 'h264-mp4', label: 'H.264 / MP4' },
];

function toFormState(cfg: VideoGenConfig): FormState {
  return {
    ...cfg,
    output_format: cfg.output_format ?? 'av1-webm',
    watermark_mode: cfg.watermark_mode ?? 'hidden',
    extract_manual_drag_frames: cfg.extract_manual_drag_frames ?? false,
    stabilize_manual_drag_frames: cfg.stabilize_manual_drag_frames ?? false,
    manual_drag_stabilize_model: cfg.manual_drag_stabilize_model ?? 'meta/muse-image/edit',
    seedText: String(cfg.seed),
    guidanceScaleText: String(cfg.guidance_scale),
    offsetFrontText: String(cfg.floor_y_offset_front),
    offsetRearText: String(cfg.floor_y_offset_rear),
    offsetLeftText: String(cfg.floor_y_offset_left),
    offsetRightText: String(cfg.floor_y_offset_right),
    heightOffsetFrontText: String(cfg.car_height_offset_front),
    heightOffsetRearText: String(cfg.car_height_offset_rear),
    heightOffsetLeftText: String(cfg.car_height_offset_left),
    heightOffsetRightText: String(cfg.car_height_offset_right),
    maxWidthText: String(cfg.car_max_width_frac),
    featherText: String(cfg.feather_px),
    shadowRetriesText: String(cfg.shadow_validation_retries),
    shadowDarkeningText: String(cfg.shadow_min_darkening_ratio),
    manualDragFramesText: String(cfg.manual_drag_frames),
    sampleStrideText: String(cfg.sample_stride),
    sampleStartText: String(cfg.frames_to_sample_start),
    sampleEndText: String(cfg.frames_to_sample_end),
    brightLumaText: String(cfg.background_bright_luma_threshold),
    brightRatioText: String(cfg.background_bright_pixel_ratio),
    cleanupPaddingText: String(cfg.background_cleanup_padding_frac),
    cleanupRetriesText: String(cfg.background_cleanup_validation_retries),
    cleanupCenterShiftText: String(cfg.background_cleanup_max_center_shift_frac),
    cleanupSizeChangeText: String(cfg.background_cleanup_max_size_change_frac),
    cleanupAspectChangeText: String(cfg.background_cleanup_max_aspect_change_frac),
  };
}

function parseFrac(value: string, label: string, { allowZero = true } = {}): number {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n) || n < 0 || n > 1 || (!allowZero && n <= 0)) {
    throw new Error(`${label} must be a number between ${allowZero ? '0' : '0 (exclusive)'} and 1`);
  }
  return n;
}

function parseSignedFrac(value: string, label: string, limit = 0.5): number {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n) || n < -limit || n > limit) {
    throw new Error(`${label} must be a number between ${-limit} and ${limit}`);
  }
  return n;
}

function fromFormState(state: FormState): VideoGenConfig {
  const seed = Number.parseInt(state.seedText, 10);
  if (Number.isNaN(seed) || seed < 0) {
    throw new Error('Seed must be a non-negative integer');
  }
  const feather = Number.parseInt(state.featherText, 10);
  if (Number.isNaN(feather) || feather < 0) {
    throw new Error('Feather (px) must be a non-negative integer');
  }
  const shadowRetries = Number.parseInt(state.shadowRetriesText, 10);
  if (Number.isNaN(shadowRetries) || shadowRetries < 0 || shadowRetries > 10) {
    throw new Error('Shadow validation retries must be an integer between 0 and 10');
  }
  const cleanupRetries = Number.parseInt(state.cleanupRetriesText, 10);
  if (Number.isNaN(cleanupRetries) || cleanupRetries < 0 || cleanupRetries > 10) {
    throw new Error('Cleanup validation retries must be an integer between 0 and 10');
  }
  const manualDragFrames = Number(state.manualDragFramesText);
  if (!Number.isInteger(manualDragFrames) || manualDragFrames < 2) {
    throw new Error('Manual drag frames must be an integer of at least 2');
  }
  const sampleStride = Number.parseInt(state.sampleStrideText, 10);
  if (Number.isNaN(sampleStride) || sampleStride < 1 || sampleStride > 10) {
    throw new Error('Sample stride must be an integer between 1 and 10');
  }
  const brightLuma = Number.parseInt(state.brightLumaText, 10);
  if (Number.isNaN(brightLuma) || brightLuma < 0 || brightLuma > 255) {
    throw new Error('Bright luminance threshold must be an integer between 0 and 255');
  }
  const {
    seedText: _seedText,
    guidanceScaleText: _guidanceScaleText,
    offsetFrontText: _ofr,
    offsetRearText: _orr,
    offsetLeftText: _olt,
    offsetRightText: _ort,
    heightOffsetFrontText: _hfr,
    heightOffsetRearText: _hrr,
    heightOffsetLeftText: _hlt,
    heightOffsetRightText: _hrt,
    maxWidthText: _mw,
    featherText: _f,
    shadowRetriesText: _sr,
    shadowDarkeningText: _sd,
    manualDragFramesText: _mdf,
    sampleStrideText: _ss,
    sampleStartText: _sst,
    sampleEndText: _set,
    brightLumaText: _bl,
    brightRatioText: _br,
    cleanupPaddingText: _cp,
    cleanupRetriesText: _cr,
    cleanupCenterShiftText: _cc,
    cleanupSizeChangeText: _cs,
    cleanupAspectChangeText: _ca,
    ...rest
  } = state;
  return {
    ...rest,
    seed,
    guidance_scale: parseFrac(state.guidanceScaleText, 'Guidance scale'),
    manual_drag_frames: manualDragFrames,
    sample_stride: sampleStride,
    frames_to_sample_start: parseFrac(state.sampleStartText, 'Frames to sample (start)'),
    frames_to_sample_end: parseFrac(state.sampleEndText, 'Frames to sample (end)'),
    floor_y_offset_front: parseSignedFrac(state.offsetFrontText, 'Front Y offset'),
    floor_y_offset_rear: parseSignedFrac(state.offsetRearText, 'Rear Y offset'),
    floor_y_offset_left: parseSignedFrac(state.offsetLeftText, 'Left Y offset'),
    floor_y_offset_right: parseSignedFrac(state.offsetRightText, 'Right Y offset'),
    car_height_offset_front: parseSignedFrac(state.heightOffsetFrontText, 'Front height offset'),
    car_height_offset_rear: parseSignedFrac(state.heightOffsetRearText, 'Rear height offset'),
    car_height_offset_left: parseSignedFrac(state.heightOffsetLeftText, 'Left height offset'),
    car_height_offset_right: parseSignedFrac(state.heightOffsetRightText, 'Right height offset'),
    car_max_width_frac: parseFrac(state.maxWidthText, 'Car max width fraction', { allowZero: false }),
    feather_px: feather,
    shadow_validation_retries: shadowRetries,
    shadow_min_darkening_ratio: parseFrac(
      state.shadowDarkeningText,
      'Shadow minimum darkening ratio',
    ),
    background_bright_luma_threshold: brightLuma,
    background_bright_pixel_ratio: parseFrac(state.brightRatioText, 'Bright pixel ratio'),
    background_cleanup_padding_frac: parseFrac(
      state.cleanupPaddingText,
      'Cleanup crop padding',
    ),
    background_cleanup_validation_retries: cleanupRetries,
    background_cleanup_max_center_shift_frac: parseFrac(
      state.cleanupCenterShiftText,
      'Cleanup maximum centre shift',
    ),
    background_cleanup_max_size_change_frac: parseFrac(
      state.cleanupSizeChangeText,
      'Cleanup maximum size change',
    ),
    background_cleanup_max_aspect_change_frac: parseFrac(
      state.cleanupAspectChangeText,
      'Cleanup maximum aspect change',
    ),
  };
}

function findModel(models: ModelInfo[], id: string): ModelInfo | undefined {
  return models.find((m) => m.id === id);
}

/**
 * When switching models, prior values for `duration` / `resolution` /
 * `aspect_ratio` may not be valid for the new model. Keep what's still
 * valid; otherwise fall back to the new model's defaults. For models that
 * don't expose a knob at all (`supports.resolution === false` or
 * `supports.aspect_ratio === false`) we leave the previously stored value
 * untouched — it isn't sent to fal anyway.
 */
function reconcileForModel(form: FormState, target: ModelInfo): FormState {
  return {
    ...form,
    model: target.id,
    duration: target.choices.duration.includes(form.duration) ? form.duration : target.defaults.duration,
    resolution: target.supports.resolution
      ? target.choices.resolution.includes(form.resolution)
        ? form.resolution
        : target.defaults.resolution
      : form.resolution,
    aspect_ratio: target.supports.aspect_ratio
      ? target.choices.aspect_ratio.includes(form.aspect_ratio)
        ? form.aspect_ratio
        : target.defaults.aspect_ratio
      : form.aspect_ratio,
  };
}

export function VideoSettingsPage() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [presets, setPresets] = useState<VideoGenPreset[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPresets()
      .then((list) => {
        if (cancelled) return;
        setModels(list.models);
        setPresets(list.presets);
        setDefaultId(list.default_id);
        const initial = list.presets.find((p) => p.id === list.default_id) ?? list.presets[0];
        if (initial) {
          setSelectedId(initial.id);
          setForm(toFormState(initial.config));
        }
        setError(null);
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentModel = useMemo<ModelInfo | undefined>(() => {
    if (!form) return undefined;
    return findModel(models, form.model);
  }, [models, form]);

  const selectedPreset = useMemo<VideoGenPreset | undefined>(
    () => presets.find((p) => p.id === selectedId),
    [presets, selectedId],
  );

  const handleModelChange = (modelId: string) => {
    if (!form) return;
    const target = findModel(models, modelId);
    if (!target) return;
    setForm(reconcileForModel(form, target));
  };

  const handleSelectPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    setSelectedId(id);
    setForm(toFormState(preset.config));
    setSavedAt(null);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form || !selectedId || selectedPreset?.builtin) return;
    setSaving(true);
    setError(null);
    try {
      const payload = fromFormState(form);
      const updated = await updatePreset(selectedId, { config: payload });
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setForm(toFormState(updated.config));
      setSavedAt(Date.now());
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!selectedPreset) return;
    setForm(toFormState(selectedPreset.config));
    setSavedAt(null);
    setError(null);
  };

  const handleNewPreset = async () => {
    const name = window.prompt('Name for the new preset?', 'New preset');
    if (!name || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createPreset(name.trim());
      setPresets((prev) => [...prev, created]);
      setSelectedId(created.id);
      setForm(toFormState(created.config));
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicatePreset = async () => {
    if (!form || !selectedPreset) return;
    const name = window.prompt('Name for the duplicated preset?', `${selectedPreset.name} copy`);
    if (!name || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createPreset(name.trim(), fromFormState(form));
      setPresets((prev) => [...prev, created]);
      setSelectedId(created.id);
      setForm(toFormState(created.config));
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRenamePreset = async () => {
    if (!selectedId || !selectedPreset || selectedPreset.builtin) return;
    const name = window.prompt('Rename preset', selectedPreset.name);
    if (!name || !name.trim() || name.trim() === selectedPreset.name) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePreset(selectedId, { name: name.trim() });
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedId || !selectedPreset) return;
    if (selectedPreset.builtin) {
      setError('Built-in presets cannot be deleted.');
      return;
    }
    if (presets.length <= 1) {
      setError('Cannot delete the only preset.');
      return;
    }
    if (!window.confirm(`Delete preset "${selectedPreset.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      const list = await deletePreset(selectedId);
      setPresets(list.presets);
      setDefaultId(list.default_id);
      const next = list.presets.find((p) => p.id === list.default_id) ?? list.presets[0];
      if (next) {
        setSelectedId(next.id);
        setForm(toFormState(next.config));
      }
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreBuiltinPreset = async () => {
    if (!selectedId || !selectedPreset?.builtin) return;
    if (
      !window.confirm(
        `Restore "${selectedPreset.name}" to the defaults from this deployment? All saved changes to this preset will be overwritten.`,
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const restored = await restoreBuiltinPreset(selectedId);
      setPresets((prev) => prev.map((p) => (p.id === restored.id ? restored : p)));
      setForm(toFormState(restored.config));
      setSavedAt(Date.now());
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      const list = await setDefaultPreset(selectedId);
      setDefaultId(list.default_id);
      setPresets(list.presets);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form || !currentModel || !selectedId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <div className="text-sm text-primary-400">Loading…</div>
      </div>
    );
  }

  const labelClass = 'block text-sm font-medium text-primary-800 mb-1';
  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-primary-200 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm disabled:opacity-60';
  const secondaryBtn =
    'px-3 py-2 rounded-lg border border-primary-200 bg-white text-sm text-primary-700 hover:bg-primary-50 disabled:opacity-50';

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <p className="text-sm text-primary-500 mt-1">
          Each <span className="font-medium">preset</span> is a full copy of the video-generation
          parameters (e.g. a different background type). A scan uses the <span className="font-medium">default</span>{' '}
          preset unless overridden when generating. Changes take effect on the next scan submitted
          (jobs already running keep the snapshot they started with).
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-soft space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className={labelClass}>Preset</label>
            <select
              value={selectedId}
              onChange={(e) => handleSelectPreset(e.target.value)}
              disabled={saving}
              className={inputClass}
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.builtin ? ' (built-in)' : ''}
                  {p.id === defaultId ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handleNewPreset} disabled={saving} className={secondaryBtn}>
              New
            </button>
            <button type="button" onClick={handleDuplicatePreset} disabled={saving} className={secondaryBtn}>
              Duplicate
            </button>
            <button
              type="button"
              onClick={handleRenamePreset}
              disabled={saving || !!selectedPreset?.builtin}
              title={selectedPreset?.builtin ? 'Built-in presets are managed in code' : undefined}
              className={secondaryBtn}
            >
              Rename
            </button>
            <button
              type="button"
              onClick={handleSetDefault}
              disabled={saving || selectedId === defaultId}
              className={secondaryBtn}
            >
              Set default
            </button>
            {selectedPreset?.builtin && (
              <button
                type="button"
                onClick={handleRestoreBuiltinPreset}
                disabled={saving}
                className={secondaryBtn}
              >
                Restore code defaults
              </button>
            )}
            <button
              type="button"
              onClick={handleDeletePreset}
              disabled={saving || presets.length <= 1 || !!selectedPreset?.builtin}
              title={selectedPreset?.builtin ? 'Built-in presets cannot be deleted' : undefined}
              className="px-3 py-2 rounded-lg border border-danger-200 bg-white text-sm text-danger-700 hover:bg-danger-50 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
        <p className="text-xs text-primary-500">
          Editing preset <span className="font-medium">{selectedPreset?.name}</span>
          {selectedId === defaultId ? ' (this is the default used for new scans)' : ''}.
        </p>
        {selectedPreset?.builtin && (
          <p className="text-xs text-primary-500">
            Built-in presets are read-only and synchronized from code whenever a service instance starts.
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
      )}
      {savedAt && !error && (
        <div className="rounded-xl border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700">
          Saved at {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-slate-200 rounded-xl p-6 shadow-soft">
        <fieldset disabled={saving || !!selectedPreset?.builtin} className="contents">
        <div>
          <label className={labelClass}>Model</label>
          <select
            value={form.model}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={saving}
            className={inputClass}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-primary-500">
            <span className="font-mono">{currentModel.endpoint}</span>
            {currentModel.description && <> — {currentModel.description}</>}
          </p>
        </div>

        <div>
          <label className={labelClass}>
            Prompt {form.compose_mode === 'no_background' ? '(no background mode)' : '(with background / off modes)'}
          </label>
          {form.compose_mode === 'no_background' ? (
            <textarea
              value={form.prompt_no_background}
              onChange={(e) => setForm({ ...form, prompt_no_background: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          ) : (
            <textarea
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          )}
          <p className="mt-1 text-xs text-primary-500">
            The positive prompt follows the preprocessing Mode selected below.{' '}
            <code>no_background</code> places the car on a blank white canvas, so its prompt must not
            mention a floor, ground, turntable or shadows. <code>with_background</code> and{' '}
            <code>off</code> share the floor-aware prompt. The negative prompt is shared across modes.
          </p>
        </div>

        <div>
          <label className={labelClass}>Per-pair prompt template</label>
          <textarea
            value={form.pair_prompt_template}
            onChange={(e) => setForm({ ...form, pair_prompt_template: e.target.value })}
            disabled={saving}
            rows={2}
            className={`${inputClass} font-mono`}
          />
          <p className="mt-1 text-xs text-primary-500">
            Prepended independently to each clip. Use <code>{'{start_view}'}</code> and{' '}
            <code>{'{end_view}'}</code>; for example, left → rear is rendered before the base
            prompt for that call. Leave blank to disable.
          </p>
        </div>

        {currentModel.supports.negative_prompt && (
          <div>
            <label className={labelClass}>Negative prompt</label>
            <textarea
              value={form.negative_prompt}
              onChange={(e) => setForm({ ...form, negative_prompt: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Duration (per pair)</label>
            <select
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              disabled={saving}
              className={inputClass}
            >
              {currentModel.choices.duration.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          {currentModel.supports.resolution && (
            <div>
              <label className={labelClass}>Resolution</label>
              <select
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.resolution.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          )}
          {currentModel.supports.aspect_ratio && (
            <div>
              <label className={labelClass}>Aspect ratio</label>
              <select
                value={form.aspect_ratio}
                onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.aspect_ratio.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Seed</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.seedText}
              onChange={(e) => setForm({ ...form, seedText: e.target.value })}
              disabled={saving}
              className={`${inputClass} font-mono`}
            />
          </div>
          {currentModel.supports.guidance_scale && (
            <div>
              <label className={labelClass}>Guidance scale</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.guidanceScaleText}
                onChange={(e) => setForm({ ...form, guidanceScaleText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">
                Kling <code>cfg_scale</code>; 0–1, default 0.5.
              </p>
            </div>
          )}
          {currentModel.supports.generate_audio && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.generate_audio}
                onChange={(e) => setForm({ ...form, generate_audio: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Generate audio</span>
            </label>
          )}
          {currentModel.supports.auto_fix && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.auto_fix}
                onChange={(e) => setForm({ ...form, auto_fix: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Auto-fix</span>
            </label>
          )}
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Viewer</h2>
            <p className="mt-1 text-xs text-primary-500">
              Manual drag snaps the completed video to uniformly spaced positions. Extract writes
              those stills and unlocks the Frames viewer. Stabilize sharpens every extracted still
              in parallel.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Manual drag frames</label>
              <input
                type="number"
                min={2}
                step={1}
                value={form.manualDragFramesText}
                onChange={(e) => setForm({ ...form, manualDragFramesText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">
                Uniform positions around the full rotation; default 32.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <label className="flex items-center gap-2 pb-2">
              <input
                type="checkbox"
                checked={Boolean(form.extract_manual_drag_frames)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    extract_manual_drag_frames: e.target.checked,
                    stabilize_manual_drag_frames: e.target.checked
                      ? form.stabilize_manual_drag_frames
                      : false,
                  })
                }
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Extract sampled frames</span>
            </label>
            <label className="flex items-center gap-2 pb-2">
              <input
                type="checkbox"
                checked={Boolean(form.stabilize_manual_drag_frames)}
                onChange={(e) =>
                  setForm({ ...form, stabilize_manual_drag_frames: e.target.checked })
                }
                disabled={saving || !form.extract_manual_drag_frames}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Stabilize extracted frames</span>
            </label>
            <div>
              <label className={labelClass}>Stabilize model</label>
              <select
                value={form.manual_drag_stabilize_model ?? 'meta/muse-image/edit'}
                onChange={(e) =>
                  setForm({ ...form, manual_drag_stabilize_model: e.target.value })
                }
                disabled={saving || !form.extract_manual_drag_frames || !form.stabilize_manual_drag_frames}
                className={inputClass}
              >
                {SHADOW_MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Output</h2>
            <p className="mt-1 text-xs text-primary-500">
              AV1/WebM is the default. Both formats encode every frame as a keyframe for exact
              drag seeking. Hidden adds the machine-detectable ownership marker; Visible also adds
              the UVeye logo.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Video format</label>
              <select
                value={form.output_format}
                onChange={(e) =>
                  setForm({ ...form, output_format: e.target.value as VideoOutputFormat })
                }
                disabled={saving}
                className={inputClass}
              >
                {OUTPUT_FORMATS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Watermark</label>
              <select
                value={form.watermark_mode}
                onChange={(e) =>
                  setForm({ ...form, watermark_mode: e.target.value as WatermarkMode })
                }
                disabled={saving}
                className={inputClass}
              >
                {WATERMARK_MODES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Motion resampling</h2>
            <p className="mt-1 text-xs text-primary-500">
              The 360 is stitched from four sub-clips, so the model eases the car to a stop at the
              end of each one — producing a periodic "slow down" at every seam. Frames near a clip's
              ends are dropped to speed those regions back up; the decimation ramps in{' '}
              <span className="font-medium">gradually</span> (none at the boundary → up to the stride
              at the very end) so the speed-up is smooth with no jump. A stride of 1 disables it.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Sample stride</label>
              <input
                type="number"
                min={1}
                max={10}
                step={1}
                value={form.sampleStrideText}
                onChange={(e) => setForm({ ...form, sampleStrideText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Max stride at the very end (ramped in).</p>
            </div>
            <div>
              <label className={labelClass}>Sample start (frac)</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.sampleStartText}
                onChange={(e) => setForm({ ...form, sampleStartText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Fraction of frames from the start.</p>
            </div>
            <div>
              <label className={labelClass}>Sample end (frac)</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.sampleEndText}
                onChange={(e) => setForm({ ...form, sampleEndText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Fraction of frames from the end (e.g. 0.1).</p>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Seam matching</h2>
            <p className="mt-1 text-xs text-primary-500">
              Each sub-clip is trimmed to the frame that best matches the next pose.{' '}
              <span className="font-medium">api</span> cuts the silhouette of every candidate frame
              plus the target, which is{' '}
              <span className="font-medium">~128 billed background-remover calls per job</span> — the
              single largest line item in a run. <span className="font-medium">local</span> derives
              the same silhouettes on the CPU. <span className="font-medium">direct</span> drops
              silhouettes altogether and correlates the frames' edges, which also catches the
              position and scale drift a silhouette normalises away. Both alternatives are free.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Match on</label>
              <select
                value={form.seam_match ?? 'api'}
                onChange={(e) => setForm({ ...form, seam_match: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {SEAM_MATCH_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-primary-500">{SEAM_MATCH_HINTS[form.seam_match] ?? SEAM_MATCH_HINTS.api}</p>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Keyframe preprocessing</h2>
            <p className="mt-1 text-xs text-primary-500">
              {form.compose_mode === 'with_background' && (
                <>
                  <strong>with background</strong>:{' '}
                  {form.placement_mode === 'model'
                    ? 'send the vehicle and room as separate references to an image model and use its generated composite directly. Candidates that do not preserve the vehicle are retried.'
                    : "matte the car, align it to the four poses' median size/position, and composite it deterministically onto the room plate."}
                </>
              )}
              {form.compose_mode === 'no_background' && (
                <>
                  <strong>no background</strong>: matte the car, auto-calibrate its size/position to
                  the four poses' median, and composite it onto a blank white canvas — the
                  background is removed entirely. No plate, no shadow, no per-pose offsets.
                </>
              )}
              {form.compose_mode === 'off' && (
                <>
                  <strong>off</strong>: preprocessing disabled. The four poses pass straight through
                  to the video model.
                </>
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Mode</label>
              <select
                value={form.compose_mode}
                onChange={(e) => {
                  const compose_mode = e.target.value;
                  setForm({
                    ...form,
                    compose_mode,
                    placement_mode:
                      compose_mode === 'with_background' ? form.placement_mode : 'median',
                  });
                }}
                disabled={saving}
                className={inputClass}
              >
                {COMPOSE_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            {form.compose_mode === 'with_background' && (
              <div>
                <label className={labelClass}>Placement</label>
                <select
                  value={form.placement_mode}
                  onChange={(e) => setForm({ ...form, placement_mode: e.target.value })}
                  disabled={saving}
                  className={inputClass}
                >
                  {PLACEMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {form.compose_mode !== 'off' && form.placement_mode !== 'model' && (
              <div>
                <label className={labelClass}>Max width (frac)</label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.02}
                  value={form.maxWidthText}
                  onChange={(e) => setForm({ ...form, maxWidthText: e.target.value })}
                  disabled={saving}
                  className={`${inputClass} font-mono`}
                />
              </div>
            )}
            {form.compose_mode !== 'off' && form.placement_mode !== 'model' && (
              <label className="flex items-center gap-2 pb-2 sm:col-span-2 sm:self-end">
                <input
                  type="checkbox"
                  checked={form.normalize_exposure}
                  onChange={(e) => setForm({ ...form, normalize_exposure: e.target.checked })}
                  disabled={saving}
                  className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-primary-800">Normalize exposure</span>
              </label>
            )}
            {form.compose_mode !== 'off' && form.placement_mode !== 'model' && (
              <label className="flex items-center gap-2 pb-2 sm:self-end">
                <input
                  type="checkbox"
                  checked={form.soft_alpha_blend}
                  onChange={(e) => setForm({ ...form, soft_alpha_blend: e.target.checked })}
                  disabled={saving}
                  className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-primary-800">Soft alpha blend</span>
              </label>
            )}
            {form.compose_mode !== 'off' && form.placement_mode !== 'model' && (
              <div>
                <label className={labelClass}>Edge feather (px)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={form.featherText}
                  onChange={(e) => setForm({ ...form, featherText: e.target.value })}
                  disabled={saving || !form.soft_alpha_blend}
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  Only used when Soft alpha blend is enabled.
                </p>
              </div>
            )}
          </div>

          {form.compose_mode === 'with_background' && form.placement_mode === 'model' && (
            <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-primary-900">Model placement</h3>
                <p className="mt-1 text-xs text-primary-500">
                  The vehicle and room are sent as separate references and the generated image is
                  used directly.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Model</label>
                  <select
                    value={form.placement_model}
                    onChange={(e) => setForm({ ...form, placement_model: e.target.value })}
                    disabled={saving}
                    className={inputClass}
                  >
                    {IMAGE_EDIT_MODELS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>GPT quality</label>
                  <select
                    value={form.placement_gpt_quality}
                    onChange={(e) => setForm({ ...form, placement_gpt_quality: e.target.value })}
                    disabled={saving || form.placement_model !== 'gpt-image-2'}
                    className={inputClass}
                  >
                    {SHADOW_QUALITIES.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Resolution</label>
                  <select
                    value={form.placement_resolution}
                    onChange={(e) => setForm({ ...form, placement_resolution: e.target.value })}
                    disabled={saving || form.placement_model !== 'nano-banana-2'}
                    className={inputClass}
                  >
                    {SHADOW_RESOLUTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Placement prompt</label>
                <textarea
                  value={form.placement_prompt}
                  onChange={(e) => setForm({ ...form, placement_prompt: e.target.value })}
                  disabled={saving}
                  rows={7}
                  className={`${inputClass} font-mono`}
                />
              </div>
            </div>
          )}

          {form.compose_mode !== 'off' && form.placement_mode !== 'model' && (
            <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-4 space-y-4">
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.adaptive_background_cleanup}
                    onChange={(e) =>
                      setForm({ ...form, adaptive_background_cleanup: e.target.checked })
                    }
                    disabled={saving}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-primary-900">
                    Adaptive background cleanup
                  </span>
                </label>
                <p className="mt-1 text-xs text-primary-500">
                  Inspect the top 10% of every keyframe. If at least three of four poses are not
                  bright, all four receive a billed 4:3 white-studio edit before normal matting.
                  Left/right mirror flips are retried; a failed edit or validation falls back
                  atomically to the original poses.
                </p>
              </div>

              {form.adaptive_background_cleanup && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                      <label className={labelClass}>Bright luminance</label>
                      <input
                        type="number"
                        min={0}
                        max={255}
                        step={1}
                        value={form.brightLumaText}
                        onChange={(e) => setForm({ ...form, brightLumaText: e.target.value })}
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Bright pixels (ratio)</label>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={form.brightRatioText}
                        onChange={(e) => setForm({ ...form, brightRatioText: e.target.value })}
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Cleanup model</label>
                      <select
                        value={form.background_cleanup_model}
                        onChange={(e) =>
                          setForm({ ...form, background_cleanup_model: e.target.value })
                        }
                        disabled={saving}
                        className={inputClass}
                      >
                        {IMAGE_EDIT_MODELS.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>gpt-image-2 quality</label>
                      <select
                        value={form.background_cleanup_gpt_quality}
                        onChange={(e) =>
                          setForm({ ...form, background_cleanup_gpt_quality: e.target.value })
                        }
                        disabled={saving || form.background_cleanup_model !== 'gpt-image-2'}
                        className={inputClass}
                      >
                        {SHADOW_QUALITIES.map((quality) => (
                          <option key={quality} value={quality}>
                            {quality}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Nano Banana resolution</label>
                      <select
                        value={form.background_cleanup_resolution}
                        onChange={(e) =>
                          setForm({ ...form, background_cleanup_resolution: e.target.value })
                        }
                        disabled={saving || form.background_cleanup_model !== 'nano-banana-2'}
                        className={inputClass}
                      >
                        {SHADOW_RESOLUTIONS.map((resolution) => (
                          <option key={resolution} value={resolution}>
                            {resolution}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Crop padding (frac)</label>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={form.cleanupPaddingText}
                        onChange={(e) =>
                          setForm({ ...form, cleanupPaddingText: e.target.value })
                        }
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                      <p className="mt-1 text-xs text-primary-500">
                        Added around the detected vehicle before the cleanup call.
                      </p>
                    </div>
                    <div>
                      <label className={labelClass}>Direction retries</label>
                      <input
                        type="number"
                        min={0}
                        max={10}
                        step={1}
                        value={form.cleanupRetriesText}
                        onChange={(e) =>
                          setForm({ ...form, cleanupRetriesText: e.target.value })
                        }
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                      <p className="mt-1 text-xs text-primary-500">
                        Reruns a side pose when its visible mirror changes sides.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Max centre shift</label>
                      <input
                        type="number"
                        min={0}
                        max={0.5}
                        step={0.01}
                        value={form.cleanupCenterShiftText}
                        onChange={(e) =>
                          setForm({ ...form, cleanupCenterShiftText: e.target.value })
                        }
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Max size change</label>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={form.cleanupSizeChangeText}
                        onChange={(e) =>
                          setForm({ ...form, cleanupSizeChangeText: e.target.value })
                        }
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Max aspect change</label>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={form.cleanupAspectChangeText}
                        onChange={(e) =>
                          setForm({ ...form, cleanupAspectChangeText: e.target.value })
                        }
                        disabled={saving}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Background cleanup prompt</label>
                    <textarea
                      value={form.background_cleanup_prompt}
                      onChange={(e) =>
                        setForm({ ...form, background_cleanup_prompt: e.target.value })
                      }
                      disabled={saving}
                      rows={7}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {form.compose_mode === 'with_background' && (
            <>
              {form.placement_mode !== 'model' && (
                <>
                  <div>
                <p className="mb-2 text-xs text-primary-500">
                  Per-pose vertical nudge (fraction of height, −0.5…0.5), added on top of the auto
                  floor line. Positive moves the car down. Each pose is independent, so sides
                  (<code>left</code>/<code>right</code>) and fronts (<code>front</code>/
                  <code>rear</code>) can sit a little differently. <code>0</code> = no shift.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Front Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetFrontText}
                      onChange={(e) => setForm({ ...form, offsetFrontText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Rear Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetRearText}
                      onChange={(e) => setForm({ ...form, offsetRearText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Left Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetLeftText}
                      onChange={(e) => setForm({ ...form, offsetLeftText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Right Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetRightText}
                      onChange={(e) => setForm({ ...form, offsetRightText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </div>
                  </div>

                  <div>
                <p className="mb-2 text-xs text-primary-500">
                  Per-pose height nudge (fraction of height, −0.5…0.5), added on top of the auto
                  median car height. Positive enlarges the car. Pair this with the Y offset so the
                  physical effect (a pose nudged forward/down also looks a touch bigger) makes sense.
                  <code>0</code> = no change.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Front height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetFrontText}
                      onChange={(e) => setForm({ ...form, heightOffsetFrontText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Rear height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetRearText}
                      onChange={(e) => setForm({ ...form, heightOffsetRearText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Left height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetLeftText}
                      onChange={(e) => setForm({ ...form, heightOffsetLeftText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Right height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetRightText}
                      onChange={(e) => setForm({ ...form, heightOffsetRightText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.generative_shadow}
                    onChange={(e) => setForm({ ...form, generative_shadow: e.target.checked })}
                    disabled={saving}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-primary-800">Generative shadow</span>
                </label>
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.add_contact_shadow}
                    onChange={(e) => setForm({ ...form, add_contact_shadow: e.target.checked })}
                    disabled={saving}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-primary-800">Synthetic shadow</span>
                </label>
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.use_full_generative_shadow_output}
                    onChange={(e) =>
                      setForm({ ...form, use_full_generative_shadow_output: e.target.checked })
                    }
                    disabled={saving || !form.generative_shadow}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-primary-800">Use full model output</span>
                </label>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className={labelClass}>Shadow model</label>
                  <select
                    value={form.shadow_model}
                    onChange={(e) => setForm({ ...form, shadow_model: e.target.value })}
                    disabled={saving}
                    className={inputClass}
                  >
                    {SHADOW_MODELS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Shadow aspect ratio</label>
                  <select
                    value={form.shadow_aspect_ratio}
                    onChange={(e) => setForm({ ...form, shadow_aspect_ratio: e.target.value })}
                    disabled={saving || !form.generative_shadow}
                    className={inputClass}
                  >
                    {SHADOW_ASPECT_RATIOS.map((ratio) => (
                      <option key={ratio} value={ratio}>
                        {ratio}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-primary-500">
                    Auto keeps the input dimensions; an explicit ratio keeps the model canvas as
                    the final keyframe.
                  </p>
                </div>
                <div>
                  <label className={labelClass}>gpt-image-2 quality</label>
                  <select
                    value={form.shadow_gpt_quality}
                    onChange={(e) => setForm({ ...form, shadow_gpt_quality: e.target.value })}
                    disabled={saving || form.shadow_model !== 'gpt-image-2'}
                    className={inputClass}
                  >
                    {SHADOW_QUALITIES.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Shadow resolution</label>
                  <select
                    value={form.shadow_resolution}
                    onChange={(e) => setForm({ ...form, shadow_resolution: e.target.value })}
                    disabled={saving || form.shadow_model !== 'nano-banana-2'}
                    className={inputClass}
                  >
                    {SHADOW_RESOLUTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Validation retries</label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    step={1}
                    value={form.shadowRetriesText}
                    onChange={(e) => setForm({ ...form, shadowRetriesText: e.target.value })}
                    disabled={saving || !form.generative_shadow}
                    className={`${inputClass} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelClass}>Min floor darkening</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.001}
                    value={form.shadowDarkeningText}
                    onChange={(e) => setForm({ ...form, shadowDarkeningText: e.target.value })}
                    disabled={saving || !form.generative_shadow}
                    className={`${inputClass} font-mono`}
                  />
                </div>
                  </div>

                  <div>
                <label className={labelClass}>Shadow prompt (generative shadow)</label>
                <textarea
                  value={form.shadow_prompt}
                  onChange={(e) => setForm({ ...form, shadow_prompt: e.target.value })}
                  disabled={saving}
                  rows={3}
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  When Generative shadow is on, the car is composited onto the plate and the chosen
                  model (nano-banana-2, gpt-image-2, or Meta Muse Image) adds a realistic floor shadow.{' '}
                  <strong>Use full model output</strong> preserves the complete model result, including
                  background replacement through windows, with the risk that other car or background
                  pixels may change. When off, only floor darkening is kept and the original car is
                  restored. Outputs that do not darken the floor are retried; the final output is
                  accepted after retries are exhausted.
                </p>
                  </div>
                </>
              )}

              <div>
                <label className={labelClass}>Background URL 1 — front (plate)</label>
                <input
                  type="url"
                  value={form.background_url}
                  onChange={(e) => setForm({ ...form, background_url: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (clean full-frame studio incl. floor)"
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  The full background the car is composited onto (floor included). Required for this
                  mode. Used for every pose when URL 2 is empty.
                </p>
              </div>

              <div>
                <label className={labelClass}>Background URL 2 — left (plate)</label>
                <input
                  type="url"
                  value={form.background_url_secondary}
                  onChange={(e) => setForm({ ...form, background_url_secondary: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (optional second plate)"
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  Optional two-background mode: <code>front</code>/<code>rear</code> use URL 1 and{' '}
                  <code>left</code>/<code>right</code> use URL 2. To use four backgrounds, also fill
                  URLs 3 and 4.
                </p>
              </div>

              <div>
                <label className={labelClass}>Background URL 3 — rear (plate)</label>
                <input
                  type="url"
                  value={form.background_url_tertiary}
                  onChange={(e) => setForm({ ...form, background_url_tertiary: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (optional; requires URL 4)"
                  className={`${inputClass} font-mono`}
                />
              </div>

              <div>
                <label className={labelClass}>Background URL 4 — right (plate)</label>
                <input
                  type="url"
                  value={form.background_url_quaternary}
                  onChange={(e) => setForm({ ...form, background_url_quaternary: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (optional; requires URL 3)"
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  With all four URLs set, each pose uses its matching background in cycle order:{' '}
                  <code>front</code>, <code>left</code>, <code>rear</code>, <code>right</code>.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-primary-100">
          <button
            type="button"
            onClick={handleRevert}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
          >
            Revert changes
          </button>
          <button
            type="submit"
            disabled={saving || !!selectedPreset?.builtin}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60"
          >
            {selectedPreset?.builtin ? 'Managed in code' : saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        </fieldset>
      </form>
    </div>
  );
}
